import { z } from 'zod'
import {
	LoanInterestDaysMonth,
	LoanInterestDaysYear,
} from '@viilu-web/shared/enums/loans'
import type { IntlShape } from 'react-intl'
import { getRequiredFields } from 'util/getZodSchemaRequiredFields'
import type { LoanApartmentWithShareholderFields } from '../types'
import { numberField } from 'util/zodSchemas'
import type { LoanShareholder } from '@viilu-web/shared/types/loans'
import { LoanApiStrictSchema } from '@viilu-web/shared/types/loans'

// In natural language: just under 10 billion
const DB_MAX_ALLOWED_AMOUNT = 9999999999

// Use a base schema for type inference, localizations would otherwise break it
const loanBaseShape = LoanApiStrictSchema.shape

export const loanFormBaseSchema = z.object({
	uuid: loanBaseShape.uuid.optional(),
	name: loanBaseShape.name.trim(),
	companyUUID: loanBaseShape.companyUUID.trim(),
	companyName: loanBaseShape.companyName.trim(),
	financingType: loanBaseShape.financingType,
	startDateEstimated: loanBaseShape.startDateEstimated.trim(),
	endDateEstimated: loanBaseShape.endDateEstimated.trim(),
	originalLoanAmount: loanBaseShape.originalLoanAmount,
	sharesPayable: loanBaseShape.sharesPayable,
	sharePaymentDates: loanBaseShape.sharePaymentDates.optional(),
	usableOnBuildingManagerCertificate:
		loanBaseShape.usableOnBuildingManagerCertificate,
	interestDaysMonth: loanBaseShape.interestDaysMonth,
	interestDaysYear: loanBaseShape.interestDaysYear,
	referenceNumber: loanBaseShape.referenceNumber.trim(),
	status: loanBaseShape.status,
	currency: loanBaseShape.currency,
	startDate: loanBaseShape.startDate.trim(),
	endDate: loanBaseShape.endDate.trim(),
	type: loanBaseShape.type.trim(),
	grantedBy: loanBaseShape.grantedBy.trim(),
	duration: loanBaseShape.duration.trim(),
	amountDrawn: loanBaseShape.amountDrawn,
	dateDrawn: loanBaseShape.dateDrawn.trim(),
	drawn: loanBaseShape.drawn,
	interestType: loanBaseShape.interestType.trim(),
	startingInterestPercentage: loanBaseShape.startingInterestPercentage,
	interestFirstDueDate: loanBaseShape.interestFirstDueDate.trim(),
	interestInspectionDates: loanBaseShape.interestInspectionDates,
	marginalPercentage: loanBaseShape.marginalPercentage,
	amortizationAmount: loanBaseShape.amortizationAmount,
	amortizationFirstDueDate: loanBaseShape.amortizationFirstDueDate.trim(),
	amortizationDates: loanBaseShape.amortizationDates,
	startingUnits: loanBaseShape.startingUnits,
	billingBasis: loanBaseShape.billingBasis,
	currentFormStep: loanBaseShape.currentFormStep.optional(),
	reachedFormStep: loanBaseShape.reachedFormStep.optional(),
})

export const requiredFormFields = getRequiredFields(loanFormBaseSchema)

// Extend base schema with localized error messages
export const loanFormSchemaLocalized = (intl: IntlShape) => {
	const s = loanFormBaseSchema
	return loanFormBaseSchema.extend({
		name: s.shape.name.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Lainan nimi vaaditaan.',
				description: 'Error message for required loan name',
			}),
		}),
		startDateEstimated: s.shape.startDateEstimated.date(
			intl.formatMessage({
				defaultMessage: 'Lainan arvioitu nostopäivä vaaditaan.',
				description: 'Error message for required loan start date',
			}),
		),
		endDateEstimated: s.shape.endDateEstimated.date(
			intl.formatMessage({
				defaultMessage: 'Lainan arvioitu alkupäivä vaaditaan.',
				description: 'Error message for required loan end date',
			}),
		),
		originalLoanAmount: numberField(intl, {
			required: { value: true },
			allowNegative: { value: false },
			max: { value: DB_MAX_ALLOWED_AMOUNT },
		}),
		sharesPayable: z.boolean({
			required_error: intl.formatMessage({
				defaultMessage: 'Tieto lainaosuuden maksettavuudesta vaaditaan.',
				description:
					'Error message for required loan share can be paid off boolean',
			}),
		}),
		usableOnBuildingManagerCertificate: z.boolean({
			required_error: intl.formatMessage({
				defaultMessage:
					'Tieto isännöitsijätodistuksella näyttämisestä vaaditaan.',
				description:
					'Error message for required building manager certificate boolean',
			}),
		}),
		interestDaysMonth: z.nativeEnum(LoanInterestDaysMonth, {
			required_error: intl.formatMessage({
				defaultMessage: 'Korkopäivät kuukaudessa vaaditaan.',
				description: 'Error message for required interest days in month',
			}),
		}),
		interestDaysYear: z.nativeEnum(LoanInterestDaysYear, {
			required_error: intl.formatMessage({
				defaultMessage: 'Korkopäivät vuodessa vaaditaan.',
				description: 'Error message for required interest days in year',
			}),
		}),
		referenceNumber: s.shape.referenceNumber.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Lainan numero vaaditaan.',
				description: 'Error message for required credit ID',
			}),
		}),
		startDate: s.shape.startDate.date(
			intl.formatMessage({
				defaultMessage: 'Lainan alkupäivä vaaditaan.',
				description: 'Error message for required loan start date',
			}),
		),
		endDate: s.shape.endDate.date(
			intl.formatMessage({
				defaultMessage: 'Lainan loppupäivä vaaditaan.',
				description: 'Error message for required loan end date',
			}),
		),
		type: s.shape.type.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Lainatyyppi vaaditaan.',
				description: 'Error message for required loan type',
			}),
		}),
		grantedBy: s.shape.grantedBy.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Lainanantaja vaaditaan.',
				description: 'Error message for required loan granted by',
			}),
		}),
		duration: s.shape.duration.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Laina-aika vaaditaan.',
				description: 'Error message for required loan duration',
			}),
		}),
		amountDrawn: numberField(intl, {
			required: { value: true },
			allowNegative: { value: false },
			max: { value: DB_MAX_ALLOWED_AMOUNT },
		}),
		dateDrawn: s.shape.dateDrawn.date(
			intl.formatMessage({
				defaultMessage: 'Ensimmäinen nostopäivämäärä vaaditaan.',
				description: 'Error message for required first loan draw date',
			}),
		),
		drawn: z.boolean({
			required_error: intl.formatMessage({
				defaultMessage: 'Tieto lainan nostotilasta vaaditaan.',
				description: 'Error message for required loan drawn status',
			}),
		}),
		interestType: s.shape.interestType.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Korkosidos vaaditaan.',
				description: 'Error message for required interest type',
			}),
		}),
		interestPercentage: numberField(intl, {
			required: { value: true },
			allowNegative: { value: true },
			max: { value: DB_MAX_ALLOWED_AMOUNT },
		}),
		interestFirstDueDate: s.shape.interestFirstDueDate.date(
			intl.formatMessage({
				defaultMessage: '1. koronmaksupäivä vaaditaan.',
				description: 'Error message for required interest due date field',
			}),
		),
		interestInspectionDates: s.shape.interestInspectionDates.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Koron tarkastuspäivämäärät vaaditaan.',
				description: 'Error message for required interest inspection dates',
			}),
		}),
		marginalPercentage: numberField(intl, {
			required: { value: true },
			allowNegative: { value: true },
			max: { value: DB_MAX_ALLOWED_AMOUNT },
		}),
		amortizationAmount: numberField(intl, {
			required: { value: true },
			allowNegative: { value: false },
			max: { value: DB_MAX_ALLOWED_AMOUNT },
		}),
		amortizationFirstDueDate: s.shape.amortizationFirstDueDate.date(
			intl.formatMessage({
				defaultMessage: 'Ensimmäinen lyhennyspäivä vaaditaan.',
				description:
					'Error message for required first loan amortization due date',
			}),
		),
		amortizationDates: s.shape.amortizationDates.min(1, {
			message: intl.formatMessage({
				defaultMessage: 'Lyhennyspäivät vaaditaan.',
				description: 'Error message for required amortization dates',
			}),
		}),
	})
}

// Separate schema into parts, which are applied depending on which part of the form the user is on.
//  .superRefine must be applied after using .pick

const financingTypeLoanFormSchema = (intl: IntlShape) =>
	loanFormSchemaLocalized(intl)
		.pick({
			financingType: true,
		})
		.required()

const basicLoanFormSchema = (intl: IntlShape) =>
	loanFormSchemaLocalized(intl)
		.pick({
			// Previous phase
			financingType: true,
			// Current phase
			name: true,
			startDateEstimated: true,
			endDateEstimated: true,
			originalLoanAmount: true,
			sharesPayable: true,
			drawn: true,
			usableOnBuildingManagerCertificate: true,
			interestDaysMonth: true,
			interestDaysYear: true,
		})
		.required()
		.superRefine(({ startDateEstimated, endDateEstimated }, ctx) => {
			if (
				startDateEstimated &&
				endDateEstimated &&
				new Date(startDateEstimated) > new Date(endDateEstimated)
			) {
				ctx.addIssue({
					path: ['endDateEstimated'],
					code: z.ZodIssueCode.custom,
					message: intl.formatMessage({
						defaultMessage:
							'Arvioidun loppumispäivän on oltava nostopäivän jälkeen.',
						description:
							'Error message indicating that estimated start date must be before estimated end date',
					}),
				})
			}
		})

const generalLoanFormSchema = (intl: IntlShape) =>
	loanFormSchemaLocalized(intl)
		.pick({
			// Previous phases
			financingType: true,
			name: true,
			startDateEstimated: true,
			endDateEstimated: true,
			originalLoanAmount: true,
			sharesPayable: true,
			drawn: true,
			usableOnBuildingManagerCertificate: true,
			interestDaysMonth: true,
			interestDaysYear: true,
			// Current phase
			referenceNumber: true,
			status: true,
			type: true,
			grantedBy: true,
			duration: true,
			startDate: true,
			endDate: true,
			amountDrawn: true,
			dateDrawn: true,
			amortizationDates: true,
			amortizationAmount: true,
			amortizationFirstDueDate: true,
			interestInspectionDates: true,
			startingInterestPercentage: true,
			interestFirstDueDate: true,
			interestType: true,
			marginalPercentage: true,
		})
		.required()
		.superRefine(
			({ startDate, endDate, originalLoanAmount, amountDrawn }, ctx) => {
				if (startDate && endDate && startDate > endDate) {
					ctx.addIssue({
						path: ['endDate'],
						code: z.ZodIssueCode.custom,
						message: intl.formatMessage({
							defaultMessage: 'Loppumispäivän on oltava nostopäivän jälkeen.',
							description:
								'Error message indicating that start date must be before end date',
						}),
					})
				}
				if (
					amountDrawn &&
					originalLoanAmount &&
					amountDrawn > originalLoanAmount
				) {
					ctx.addIssue({
						path: ['amountDrawn'],
						code: z.ZodIssueCode.custom,
						message: intl.formatMessage(
							{
								defaultMessage:
									'Nostettu lainasumma ei voi olla suurempi kuin päätetty lainasumma ({amount}).',
								description:
									'Error message indicating that drawn amount cant be larger than original loan amount',
							},
							{
								amount: originalLoanAmount,
							},
						),
					})
				}
			},
		)

const sharegroupSelectionLoanFormSchema = (intl: IntlShape) =>
	loanFormSchemaLocalized(intl)
		.pick({
			// Previous phases
			financingType: true,
			name: true,
			startDateEstimated: true,
			endDateEstimated: true,
			originalLoanAmount: true,
			sharesPayable: true,
			drawn: true,
			usableOnBuildingManagerCertificate: true,
			interestDaysMonth: true,
			interestDaysYear: true,
			referenceNumber: true,
			status: true,
			type: true,
			grantedBy: true,
			duration: true,
			startDate: true,
			endDate: true,
			amountDrawn: true,
			dateDrawn: true,
			amortizationDates: true,
			amortizationAmount: true,
			amortizationFirstDueDate: true,
			interestInspectionDates: true,
			startingInterestPercentage: true,
			interestFirstDueDate: true,
			interestType: true,
			marginalPercentage: true,
			// Current phase
			billingBasis: true,
		})
		.required()

export const loanFormSchemaBySteps = (intl: IntlShape) => [
	financingTypeLoanFormSchema(intl),
	basicLoanFormSchema(intl),
	generalLoanFormSchema(intl),
	sharegroupSelectionLoanFormSchema(intl),
]

export type LoanCreateFormInputs = z.infer<typeof loanFormBaseSchema> & {
	// These are special scenarios, which we want to include in our form for processing purposes
	// even though they are not direct form fields and do not need to be validated.
	// TODO: This could be refactored to make this work nicer with Zod but that would be another PR.
	shareholders: LoanShareholder[]
	selectableSharegroups: LoanApartmentWithShareholderFields[]
}
